import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import { subscribeSaveState } from './saveBus';
import { loadRowContent } from './contentLoaders';
import styles from './LivePreviewPop.module.css';

const windowAround = (list, movedId, size = 8) => {
  if (!list || list.length <= size) return list || [];
  const mi = list.findIndex((x) => x.id === movedId);
  if (mi < 0) return list.slice(0, size);
  const start = Math.max(0, Math.min(mi - Math.floor(size / 2), list.length - size));
  return list.slice(start, start + size);
};

// Floating 16:9 "projector preview" (top-right). For a content reorder it plays
// a mini cinematic: show the projector home (name + category nav + a few rows),
// then zoom into the affected row and animate the moved item into place. For row
// / type reorders it plays the simpler old->new travel.
const LivePreviewPop = () => {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('home'); // cinematic: 'home'|'zoom' · simple: 'from'|'to'
  const [homeContent, setHomeContent] = useState({});
  const hideTimer = useRef(null);
  const stepTimers = useRef([]);
  const affectedStripRef = useRef(null);
  const clearSteps = () => { stepTimers.current.forEach(clearTimeout); stepTimers.current = []; };

  useEffect(() => subscribeSaveState((state, preview) => {
    // Only kick off on the FIRST emit ('saving'); the follow-up 'saved' event
    // carries the same payload and would otherwise restart (double-loop) the
    // animation. 'idle' hides it.
    const has = preview && (preview.affected || (preview.items && preview.items.length));
    if (state === 'saving' && has) {
      if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
      clearSteps();
      setData(preview);
      setHomeContent({});
      if (preview.affected) {
        // home (show projector) → zoom (focus the row, still old order) → swap.
        setPhase('home');
        stepTimers.current.push(setTimeout(() => setPhase('zoom'), 1150));
        stepTimers.current.push(setTimeout(() => setPhase('swap'), 1900));
      } else {
        setPhase('from');
        stepTimers.current.push(setTimeout(() => setPhase('to'), 520));
      }
    }
    if (state === 'idle') {
      hideTimer.current = setTimeout(() => setData(null), 1000);
    }
  }), []);

  // Load the home rows' thumbnails (content reorders only).
  useEffect(() => {
    if (!data || !data.affected || !data.rows) return undefined;
    let alive = true;
    Promise.all(data.rows.map((r) => loadRowContent({
      reorderType: r.reorderType, itemId: r.itemId, userId: data.userId, limit: 10,
    }).then((items) => ({ id: r.id, items })).catch(() => ({ id: r.id, items: [] }))))
      .then((results) => {
        if (!alive) return;
        const m = {};
        results.forEach((x) => { m[x.id] = x.items; });
        setHomeContent(m);
      });
    return () => { alive = false; };
  }, [data]);

  // During the swap, scroll the affected strip to the moved tile (if later).
  useEffect(() => {
    if (phase !== 'swap' || !data?.affected) return;
    const el = affectedStripRef.current;
    if (!el) return;
    const moved = el.querySelector('[data-moved="true"]');
    if (moved && moved.scrollIntoView) {
      moved.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [phase, data, homeContent]);

  // Simple (row / type / album) reorder: once it settles into the new order,
  // scroll the moved tile into view so the switch is visible even when the item
  // travelled outside the pop's visible window.
  useEffect(() => {
    if (phase !== 'to' || !data || data.affected) return;
    const el = affectedStripRef.current;
    if (!el) return;
    const moved = el.querySelector('[data-moved="true"]');
    if (moved && moved.scrollIntoView) {
      moved.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [phase, data]);

  const tile = (it, movedId) => {
    const moved = movedId && it.id === movedId;
    return (
      <motion.div
        layout
        key={it.id}
        data-moved={moved ? 'true' : undefined}
        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        className={cn(styles.tile, { [styles.tileMoved]: moved })}
      >
        <span className={styles.thumb} style={{ backgroundImage: `url(${it.image})` }} />
        <span className={styles.tileName}>{it.text || 'Untitled'}</span>
      </motion.div>
    );
  };

  const zoomed = phase === 'zoom' || phase === 'swap';
  let bodyEl = null;
  if (data && data.affected) {
    const a = data.affected;
    // Old order until the swap phase, so the move is visible as it happens.
    const affectedOrder = phase === 'swap' ? a.items : (a.prevItems || a.items);
    bodyEl = (
      <div className={cn(styles.stage, { [styles.stageZoom]: zoomed })}>
        <div className={styles.brand}>{data.projectorName}</div>
        {data.nav && data.nav.length > 0 && (
          <div className={styles.nav}>
            {data.nav.slice(0, 8).map((n) => (
              <span key={n.id} className={styles.navTile} style={n.image ? { backgroundImage: `url(${n.image})` } : undefined}>
                <span className={styles.navName}>{n.text}</span>
              </span>
            ))}
          </div>
        )}
        <div className={styles.rows}>
          {data.rows.map((r) => {
            const isAffected = r.id === a.rowId;
            const items = isAffected ? affectedOrder : (homeContent[r.id] || []);
            return (
              <div
                key={r.id}
                className={cn(styles.homeRow, {
                  [styles.rowFocused]: isAffected && zoomed,
                  [styles.rowDimmed]: !isAffected && zoomed,
                })}
              >
                <span className={styles.homeRowTitle}>{r.title}</span>
                <div className={styles.homeStrip} ref={isAffected ? affectedStripRef : undefined}>
                  {items.length > 0
                    ? items.map((it) => tile(it, isAffected ? a.movedId : null))
                    : Array.from({ length: 5 }, (_, i) => <span key={i} className={styles.tileSkeleton} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  } else if (data) {
    const newItems = data.items || [];
    const prevItems = data.prevItems || newItems;
    const windowNew = windowAround(newItems, data.movedId, 11);
    const windowIds = new Set(windowNew.map((x) => x.id));
    const windowPrev = prevItems.filter((x) => windowIds.has(x.id));
    const rows = phase === 'from' ? windowPrev : windowNew;
    bodyEl = (
      <>
        <div className={styles.rowLabel}>{data.title || 'Category'}</div>
        <div className={styles.strip} ref={affectedStripRef}>{rows.map((it) => tile(it, data.movedId))}</div>
      </>
    );
  }

  return ReactDOM.createPortal(
    <AnimatePresence>
      {data && (
        <motion.div
          className={styles.pop}
          initial={{ opacity: 0, x: 28, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 28, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        >
          <div className={styles.head}>
            <span className={styles.badge}>
              <TvRoundedIcon fontSize="inherit" />
              Projector preview
            </span>
            <span className={styles.hint}>updated</span>
          </div>
          {bodyEl}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default LivePreviewPop;
